import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * The dashboard's contact search and its "+ Add Contact" action.
 *
 * Xano searched one combined index across contacts, customers, partners and
 * corporate clients and returned them in a single `combined_record` list. The
 * new API keeps people and organizations apart, so this queries both and
 * merges them into the shape the search box already understands.
 *
 * The result decides where clicking a row lands, so the fields that drive
 * that routing — partnerType, companyName, isCustomer, linked_customer_id —
 * are set deliberately rather than passed through.
 */

type Payload<T> = AxiosRequestConfig<T>;

export type SearchContactReqType = Partial<{
  headers: { Authorization: string };
  searchQuery: string;
  contactType: string | null;
}>;

type CustomerRow = {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  type: string;
  lifecycleStage: string;
};

type OrganizationRow = {
  id: string;
  name: string;
  type: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  partnerLevel?: number | null;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export async function searchContact1(
  payload: Payload<SearchContactReqType> & {
    params?: { searchQuery?: string; contactType?: string | null };
  },
): Promise<{ data: { combined_record: any[] } }> {
  const search = (
    payload.params?.searchQuery ??
    payload.data?.searchQuery ??
    ""
  ).trim();
  if (search.length < 2) return { data: { combined_record: [] } };

  const contactType =
    payload.params?.contactType ?? payload.data?.contactType ?? null;

  // "Business" means a company rather than a person, so it skips customers
  // entirely; "individual" skips organizations. Neither means search both.
  const wantsPeople = contactType !== "business";
  const wantsOrganizations = contactType !== "individual";

  // Split the room when searching both. Eight customers named Pooja would
  // otherwise bury the partner of the same name, which is usually the one
  // being looked for.
  const perType = wantsPeople && wantsOrganizations ? 5 : 10;

  const [people, organizations] = await Promise.all([
    wantsPeople
      ? moneefloApi
          .get("/customers", { params: { search, perPage: perType } })
          .then((r) => (r.data?.items ?? []) as CustomerRow[])
          .catch(() => [] as CustomerRow[])
      : Promise.resolve([] as CustomerRow[]),
    wantsOrganizations
      ? moneefloApi
          .get("/organizations", { params: { search, perPage: perType } })
          .then((r) => (r.data?.items ?? []) as OrganizationRow[])
          .catch(() => [] as OrganizationRow[])
      : Promise.resolve([] as OrganizationRow[]),
  ]);

  const peopleRows = people.map((person) => {
    // A customer who has transacted opens their customer record; a lead who
    // has not opens the same screen, which serves both.
    const isCustomer = person.lifecycleStage === "customer";
    return {
      id: person.id,
      contactName: person.fullName,
      full_name: person.fullName,
      contactNumber: person.phone ?? "",
      phone_number: person.phone ?? "",
      contactEmail: person.email ?? "",
      contactAddress: [person.city ?? ""],
      contactPersonType: person.type,
      status: person.lifecycleStage,
      lead_status: person.lifecycleStage,
      customer: isCustomer,
      // Routing reads this to decide between the customer screen and the
      // contact screen; pointing it at the record itself keeps both on the
      // customer screen, which is the one that is migrated and works.
      linked_customer_id: person.id,
      customer_types: isCustomer ? ["Contact", "Customer"] : ["Contact"],
      name: person.fullName,
      phone: person.phone ?? "",
      company_name: null,
      type: null,
    };
  });

  const organizationRows = organizations.map((org) => {
    const isCorporate = org.type === "corporate";
    return {
      id: org.id,
      contactName: org.name,
      full_name: org.name,
      contactNumber: org.phone ?? "",
      phone_number: org.phone ?? "",
      contactEmail: org.email ?? "",
      contactAddress: [org.city ?? ""],
      contactPersonType: "business",
      status: "registered",
      lead_status: "registered",
      customer: false,
      linked_customer_id: null,
      customer_types: [],
      name: org.name,
      phone: org.phone ?? "",
      // A corporate routes by company_name, a partner by type. Setting only
      // the one that applies is what sends each to its own screen.
      company_name: isCorporate ? org.name : null,
      type: isCorporate ? null : (org.type ?? "partner"),
    };
  });

  return { data: { combined_record: [...peopleRows, ...organizationRows] } };
}

export type CreateLeadContactReqType = Partial<{
  contactName: string;
  contactNumber: string;
  contactEmail: string;
  contactAddress: string[];
  contactPersonType: string;
  notes: string;
  pipeline: string;
  linked_partner_id: number;
}>;

/**
 * Creates the contact the search offered to add.
 *
 * Xano's `contacts` and `customers` were two tables; here a contact is a
 * customer at the `lead` stage, so one record is created and it becomes a
 * customer when they transact.
 */
export async function createLeadContact(
  payload: Payload<CreateLeadContactReqType>,
): Promise<{ status: number; data: { id: string } }> {
  const body = payload.data ?? {};
  const name = body.contactName?.trim();
  if (!name) throw new Error("A contact needs a name.");
  if (!body.contactNumber?.trim() && !body.contactEmail?.trim()) {
    throw new Error("A contact needs a phone number or an email address.");
  }

  const { data } = await moneefloApi.post("/customers", {
    fullName: name,
    phone: body.contactNumber?.trim() || undefined,
    email: body.contactEmail?.trim() || undefined,
    city: body.contactAddress?.[0]?.trim() || undefined,
    type: body.contactPersonType === "business" ? "business" : "individual",
    notes: body.notes?.trim() || undefined,
    source: "dashboard_search",
  });

  // The Sheet checks response.status, which axios sets but this does not.
  return { status: 201, data: { id: (data as { id: string }).id } };
}

/* ------------------------------------ the prepare-quote contact lookup */

export type GetSearchConatctForPrepareQuoteRequestType = Partial<{
  headers: { Authorization: string };
  params: { phone_number: string };
}>;

/**
 * The number box on the "Contact details" modal, which fills in the name and
 * email before a quotation is downloaded or shared.
 *
 * Xano matched a phone number against its combined contact index. Here a
 * contact is a customer at the `lead` stage, so one query over `/customers`
 * serves it — the same place the dashboard search looks.
 *
 * The modal reads `full_name`, `phone_number` and `email` off each row and
 * `contactAddress[1]`, so they are all present; the rest of the shape
 * matches `searchContact1` above so a row can be handed between the two.
 */
export async function getContactDetailsPrepareQuote(
  payload: Payload<GetSearchConatctForPrepareQuoteRequestType>,
): Promise<{ data: Record<string, unknown>[] }> {
  const search = (payload.params?.phone_number ?? "").trim();
  // Anything shorter matches half the book, and the box only fires at ten
  // digits anyway.
  if (search.length < 6) return { data: [] };

  const { data } = await moneefloApi.get("/customers", {
    params: { search, perPage: 10 },
  });
  const customers = (data?.items ?? []) as CustomerRow[];

  return {
    data: customers.map((customer) => ({
      id: customer.id,
      full_name: customer.fullName,
      phone_number: customer.phone ?? "",
      email: customer.email ?? "",
      // The modal reads index 1; Xano's second line was the city.
      contactAddress: [customer.city ?? "", customer.city ?? ""],
      contactPersonType: customer.type,
    })),
  };
}
